/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */
import {HttpEventType} from '@angular/common/http';
import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {DISMISS_MSG, GMTVP} from '../../../common/constants';
import {UpdatePopUpComponent} from '../../../core/components/pop-ups/update-pop-up/update-pop-up.component';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {LoadingCounter} from '../../../core/helpers/loading-counter';
import {StateService} from '../../../core/state/state.service';
import {IndustryScenario} from '../../../models/industry-scenario-dto.model';
import {IndustryScenarioWorksheetModel} from '../../../models/industry-scenario-worksheet.model';
import {ScenarioCategory} from '../../../models/scenario-category.model';
import {ScenarioType} from '../../../models/scenario-type.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {ExcelExportService} from '../../../services/excel-export.service';
import {IndustryScenarioService} from '../../../services/industry-scenario.service';
import {FileHandle} from './dragDrop.directive';

export const INDUSTRY_SCENARIO_PATH = '/industry-scenario';
export const SCEN_NAME_MAX_LENGTH = 25;
export const ASSM_MAX_LENGTH = 150;

@Component({
    selector: 'app-create-industry-scenario',
    templateUrl: './create-industry-scenario.component.html',
    styleUrls: ['./create-industry-scenario.component.scss'],
    standalone: false
})
export class CreateIndustryScenarioComponent implements OnInit {

  // Constants and Page Manipulation
  scenarioNameMaxLength = SCEN_NAME_MAX_LENGTH;
  assumptionMaxLength = ASSM_MAX_LENGTH;
  fromScenario = 'From Existing Scenario';
  fromGlobal = 'From Global Scenario';
  fromTemplate = 'From Template';
  readonly loading = new LoadingCounter();

  // First Page
  scenarioCategories: ScenarioCategory[] = [];
  scenarioTypes: ScenarioType[] = [];

  scenarioName = '';
  selectedCategory = '';
  selectedType = '';
  scenarioAssumptions = '';

  // Second Page
  sourceList: string[] = [this.fromScenario, this.fromGlobal, this.fromTemplate];
  selectedSource: string;
  fcorgs: string[];
  worksheetFcorgs: string[];
  selectedFcorg: string;

  industryScenarios: IndustryScenario[] = [];

  selectedIndustryScenario: IndustryScenario;
  selectedGlobalScenario: any;
  selectedMemberScenarios: IndustryScenario[];

  // File Template Variables
  files: FileHandle[] = [];
  file: File;
  sheets: SelectableOptions = new SelectableOptions();
  excelIndustryScenarioData: Map<string, IndustryScenarioWorksheetModel[]> = new Map<string, IndustryScenarioWorksheetModel[]>();


  constructor(public snackBar: FabMessageService,
              readonly industryScenarioService: IndustryScenarioService,
              public stateService: StateService,
              readonly router: Router,
              public dialog: MatDialog,
              public readonly excelService: ExcelExportService) {
  }

  get showSpinner(): boolean {
    return this.loading.active;
  }

  /**
   * Sets the page to the default state
   */
  ngOnInit(): void {
    this.selectedSource = this.fromScenario;

    this.getFcorgsByGmid();
    this.getScenarioTypes();
    this.getScenarioCategories();
  }

  /**
   * Calls service function to get all scenario types
   */
  getScenarioTypes(): void {
    this.loading.track(this.industryScenarioService.getAllScenarioTypes()).subscribe(
      data => {
        this.scenarioTypes = data.map(entry => entry.SCEN_TYPE);
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      });
  }

  /**
   * Calls service function to get all scenario categories
   */
  getScenarioCategories(): void {
    this.loading.track(this.industryScenarioService.getAllScenarioCategories()).subscribe(
      data => {
        this.scenarioCategories = data.map(entry => entry.scenarioCategory);
      },
      (err) => {
        this.snackBar.displayError(err, DISMISS_MSG);
      }
    );
  }

  /**
   * Get list of FCORGs that the user has access to
   */
  getFcorgsByGmid(): void {
    this.loading.track(this.industryScenarioService.getFcorgsByGmid(this.stateService.getUserGmid())).subscribe(data => {
        this.fcorgs = data;
        this.worksheetFcorgs = data.filter(item => item !== GMTVP);
      },
      (error) => {
        this.snackBar.displayError(error, DISMISS_MSG);
      });
  }

  /**
   * Calls a certain update function based on the selected source
   */
  onSourceChange(): void {
    switch (this.selectedSource) {
      case this.fromScenario:
        this.resetBaseIndustryScenarios();
        break;
      case this.fromGlobal:
        this.resetGlobalIndustryScenarios();
        break;
      case this.fromTemplate:
        this.setWorksheetView();

    }
  }

  /**
   * Resets data to defaults for creating via global scenario
   */
  resetGlobalIndustryScenarios(): void {
    this.industryScenarios = [];
    this.selectedMemberScenarios = [];
    this.selectedFcorg = GMTVP;
  }

  /**
   * Resets data to defaults for creating via base scenario and retrieves fcorgs and industry scenarios
   */
  resetBaseIndustryScenarios(): void {
    this.selectedFcorg = undefined;
    this.selectedIndustryScenario = null;
    this.getFcorgsByGmid();
  }

  /**
   * Loads the worksheet view and hides the left and right tables
   */
  setWorksheetView(): void {
    this.file = null;
    this.selectedFcorg = null;
    this.fcorgs = this.worksheetFcorgs;
  }

  /**
   * Calls the proper function to fetch the member scenarios based on the selected scenario
   * @param event The event generated by the child
   */
  onSelectGlobal(event: any) {
    this.selectedGlobalScenario = event.globalScenario;
    this.selectedMemberScenarios = event.memberScenarios;
  }

  onSelect(event: any) {
    this.selectedIndustryScenario = event.selectedScenarios[0];
  }

  /**
   * Uploads the selected file
   * @param event Map<string, any>
   */
  upload(event: any): void {
    this.sheets.options = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets.options,
      isMultiSelect: true
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
      width: '250px'
    }).afterClosed().subscribe(
      data => {
        if (data) {
          this.sheets.selectedOptions = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      () => {
        if (this.sheets.selectedOptions.length === 0) {
          this.file = null;
        }
      }
    );
  }

  /**
   * Verifies if all necessary inputs and selections have been made to enable the create button
   */
  checkInputs(): boolean {
    const areInputsFilled = this.scenarioName.trim().length !== 0 && this.scenarioAssumptions.trim().length !== 0
      && this.selectedCategory.trim().length !== 0 && this.selectedType.trim().length !== 0;
    switch (this.selectedSource) {
      case this.fromScenario:
        return areInputsFilled && this.selectedIndustryScenario != null;
      case this.fromGlobal:
        return areInputsFilled && this.selectedFcorg != null && this.selectedMemberScenarios?.length > 0;
      case this.fromTemplate:
        return areInputsFilled && this.file != null && this.selectedFcorg != null;
    }
  }

  /**
   * Opens a pop-up to update the scenario name
   * @param field the current name
   * @param maxLength the max length the field can be
   */
  updateScenarioName(field: string, maxLength: number): void {
    this.dialog.open(UpdatePopUpComponent, {
      data: {
        title: 'Update Scenario Name',
        currentValueToUpdate: field,
        maxLength,
        hidePreview: true,
        saveButtonText: 'Save',
        showCharCount: true
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.scenarioName = result.data.newValue;
      }
    });
  }

  /**
   * Opens a pop-up to update the scenario assumption
   * @param field the current assumption
   * @param maxLength the max length the field can be
   */
  updateAssumption(field: string, maxLength: number): void {
    this.dialog.open(UpdatePopUpComponent, {
      data: {
        title: 'Update Assumption',
        currentValueToUpdate: field,
        maxLength,
        hidePreview: true,
        saveButtonText: 'Save',
        showCharCount: true
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.scenarioAssumptions = result.data.newValue;
      }
    });
  }

  /**
   * Creates the industry scenario based on the selected source
   */
  createIndustryScenario(): void {
    if (this.selectedSource === this.fromGlobal) {
      this.createScenarioFromGlobalScenario();
    } else if (this.selectedSource === this.fromScenario) {
      this.createScenarioFromScenario();
    } else if (this.selectedSource === this.fromTemplate) {
      this.createScenarioFromTemplate();
    }
  }

  /**
   * Creates a new industry scenario with the selected industry scenario
   */
  createScenarioFromScenario(): void {
    this.loading.track(this.industryScenarioService.createScenarioFromScenario(this.scenarioName, this.scenarioAssumptions,
      this.selectedCategory, this.selectedType, this.stateService.getUserGmid(),
      this.selectedIndustryScenario.id.toString())).subscribe({
      error: err => {
        this.errorHandle(err);
      },
      complete: () => {
        this.showConfirmationDialog();
      }
    });
  }

  /**
   * Creates a new industry scenario from the provided member scenarios of a global scenario
   */
  createScenarioFromGlobalScenario(): void {
    // Check and see if there are multiple of the same FCORGs in the multiselected scenarios
    const allFcorgs = this.selectedMemberScenarios.map(row => row.forecastOrganization);
    const uniqueFcorgs = [...new Set(allFcorgs)];
    if (allFcorgs.length !== uniqueFcorgs.length) {
      this.snackBar.displayError('Error: You have selected multiple scenarios of the same FCORG. ' +
        'Please select one scenario per FCORG and try again.', DISMISS_MSG);
    } else {
      // parse the ID's from list of scenarios into a string to send to API
      const ids = this.selectedMemberScenarios.map(row => row.id).join(',');
      this.loading.track(this.industryScenarioService.createScenarioFromGlobalScenario(this.scenarioName,
        this.scenarioAssumptions, this.selectedCategory, this.selectedType, this.stateService.getUserGmid(), ids,
        this.selectedFcorg)).subscribe({
        error: err => {
          this.errorHandle(err);
        },
        complete: () => {
          this.showConfirmationDialog();
        }
      });
    }
  }

  /**
   * Get the industry scenario report data from the Excel file that was uploaded
   */
  createScenarioFromTemplate(): void {
    this.loading.track(this.industryScenarioService.createScenarioFromTemplate(this.file, this.sheets.selectedOptions,
      this.stateService.getUserGmid(), this.scenarioName, this.scenarioAssumptions, this.selectedFcorg,
      this.selectedCategory, this.selectedType)).subscribe(
      (e: any) => {
        // Get industry scenario data from the uploaded Excel file
        if (e.type === HttpEventType.Response) {
          this.excelIndustryScenarioData = e.body;
        }
      },
      (error) => {
        if (error.status === 0) {
          this.snackBar.displayError('Error occurred. Please re-upload file.', DISMISS_MSG);
        } else {
          this.snackBar.displayError(error, DISMISS_MSG);
        }
      },
      () => {
        this.showConfirmationDialog();
      });
  }

  /**
   * Opens a dialog window with a FabConfirmationDialogComponent and waits for it to load.
   * If the user confirms the dialog, navigates to '/industry-scenario' route with a filter fragment.
   * If the user cancels the dialog, navigates to '/industry-scenario' route without a filter fragment.
   */
  showConfirmationDialog(): void {
    const dialogRef = this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Scenario Created Successfully',
        message: 'Do you want to view the scenario created?',
        noLabel: 'No',
        yesLabel: 'Yes',
        titleIcon: 'help',
        actionColor: 'primary'
      },
      disableClose: true
    });

    // If user clicks 'Yes', navigate to home page with the newly created scenario selected
    dialogRef.componentInstance.yes.subscribe(() => {
      this.industryScenarioService.getAllIndustryScenarios().subscribe(data => {
          this.selectedIndustryScenario = data.filter(
            item => item.industryScenarioName === this.scenarioName)[0];
          this.stateService.setSelectedScenario(this.selectedIndustryScenario, false);
        },
        (err) => {
          this.snackBar.displayError(err, DISMISS_MSG);
        },
        () => {
          this.router.navigate(['']);
        }
      );
    });
    // If user clicks 'No', just navigate back to home page without selecting newly created scenario
    dialogRef.componentInstance.no.subscribe(() => {
      this.router.navigate(['']);
    });
  }

  /**
   * display message on error.
   * @param error custom error message.
   */
  errorHandle(error: string): void {
    this.snackBar.displayError(error, DISMISS_MSG);
  }
}
